import { Translation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import { z } from "zod";

import { GlobalPaginationResponse, GlobalResponseSchema } from "./global-schema";

// Login
export const LoginFormSchema = z
	.object({
		cellphone: z.string().trim().optional(),
		email: z.string().trim().optional(),
		country_id: z
			.string({
				required_error: (
					<Translation>{(t) => t(tokens.validation.countryRequired)}</Translation>
				) as unknown as string,
			})
			.trim(),
	})
	.refine(
		(data) => {
			// Either cellphone or email must be provided
			return data.cellphone || data.email;
		},
		{
			message: "Either phone number or email is required",
			path: ["cellphone"], // This will show the error on the cellphone field
		},
	)
	.refine(
		(data) => {
			// If cellphone is provided, it must be 10 digits
			if (data.cellphone) {
				return data.cellphone.length === 10;
			}
			return true;
		},
		{
			message: (
				<Translation>{(t) => t(tokens.validation.cellphoneLength)}</Translation>
			) as unknown as string,
			path: ["cellphone"],
		},
	);
export const PhoneLoginBodySchema = z.object({
	cellphone: z
		.string({
			required_error: (
				<Translation>{(t) => t(tokens.validation.cellphoneRequired)}</Translation>
			) as unknown as string,
		})
		.trim(),
	country_id: z.number({
		required_error: (
			<Translation>{(t) => t(tokens.validation.countryRequired)}</Translation>
		) as unknown as string,
	}),
});

export const EmailLoginBodySchema = z.object({
	email: z.string().email().trim(),
});

export const DeleteAccountBodySchema = z.object({
	delete_reason: z.string(),
});

export const DeleteAccountResponseSchema = GlobalResponseSchema.extend({});

export const LoginResponseSchema = GlobalResponseSchema.extend({
	data: z.object({
		status: z.number(),
		status_name: z.string(),
	}),
});
// Login Otp
export const LoginPhoneVerifyBodySchema = z.object({
	code: z.string(),
	cellphone: z.string(),
	country_id: z.number(),
	referral_code: z.string().optional(),
});

export const LoginEmailVerifyBodySchema = z.object({
	code: z.string(),
	email: z.string().email().trim(),
	referral_code: z.string().optional(),
});

export const LoginVerifyResponseSchema = GlobalResponseSchema.extend({
	user: z.object({
		user_id: z.string(),
		firstname: z.string().nullable(),
		lastname: z.string().nullable(),
		cellphone: z.string().nullable(),
		email: z.string().nullable(),
		status: z.number(),
		status_name: z.string(),
		user_type: z.number(),
		user_type_name: z.string(),
		access_token: z.string(),
		refresh_token: z.string(),
		default_length: z.string(),
		discount_referral: z
			.object({
				amount: z.number(),
				type: z.number(),
				type_name: z.string(),
			})
			.nullable(),
		default_size: z.string(),
		operator: z.object({ id: z.number() }).nullable(),
		driver: z
			.object({
				id: z.number(),
				license_number: z.string(),
				default_truck_id: z.number().nullable(),
			})
			.nullable(),
		current_carrier: z
			.object({
				id: z.number(),
				name: z.string(),
				is_self_carrier: z.boolean(),
			})
			.nullable(),
		request_carrier: z
			.object({
				id: z.number(),
				name: z.string(),
				type: z.number(),
				type_name: z.string(),
				status: z.number(),
				status_name: z.string(),
			})
			.nullable(),
	}),

	user_permissions: z.array(z.string()),
});

//
const CountrySchema = z.object({
	id: z.number(),
	name: z.string(),
	country_icon: z.string(),
	country_code: z.string(),
});

export const CountriesResponseSchema = GlobalPaginationResponse.extend({
	results: z.array(CountrySchema),
});

export const CitySchema = z.object({
	id: z.number(),
	name: z.string(),
	formatted_address: z.string().nullable().nullish(),
	state_name: z.string(),
	state_name_short: z.string(),
	is_postal_code: z.boolean().nullish(),
	state: z.object({
		country_name: z.string(),
		country_name_short: z.string(),
		created: z.string(),
		name: z.string(),
		name_short: z.string(),
		id: z.number(),
	}),
	lat: z.number(),
	lng: z.number(),
	created: z.string(),
});

export const CitiesResponseSchema = GlobalPaginationResponse.extend({
	results: z.array(CitySchema),
});
//State

export const StateSchema = z.object({
	id: z.number(),
	country_name: z.string(),
	name: z.string(),
	name_short: z.string(),
	created: z.string(),
});

export const StatesResponseSchema = GlobalPaginationResponse.extend({
	results: z.array(StateSchema),
});
//User Schema
export const UserSchema = z.object({
	firstname: z.string().nullable(),
	lastname: z.string().nullable(),
	status_name: z.string(),
	default_length: z.string(),
	default_length_symbol: z.string(),
	email: z.string().nullable(),
	default_size: z.string(),
	check_referral_code: z.boolean(),
	default_size_symbol: z.string(),
	status: z.number(),
	wallet: z.number(),
	driver: z
		.object({
			id: z.number(),
			license_number: z.string(),
			default_truck_id: z.number().nullable(),
		})
		.nullable(),
	current_carrier: z
		.object({
			id: z.number(),
			type: z.number(),
			name: z.string(),
			is_active: z.boolean(),
			is_check_lock_trucks: z.boolean(),
			is_self_carrier: z.boolean(),
		})
		.nullable(),
	request_carrier: z
		.object({
			id: z.number(),
			name: z.string(),
			status: z.number(),
			status_name: z.string(),
			type: z.number(),
			type_name: z.string(),
		})
		.nullable(),
	user_type: z.number(),
	plan: z
		.object({
			expired: z.string(),
			id: z.number(),
			days: z.number(),
			title: z.string(),
			icon: z.string().nullable(),
			is_trial: z.boolean(),
			is_access_buy_plan: z.boolean(),

			accessibilities: z
				.array(
					z.object({
						id: z.number(),
						title: z.string(),
						value: z.number(),
						remain_value: z.number(),
						created: z.string(),
					}),
				)
				.nullable(),
		})
		.nullable(),
	user_type_name: z.string(),
	cellphone: z.string().nullable(),
});

export const UserInfoResponseSchema = GlobalResponseSchema.extend({
	data: UserSchema,
});

export const ProfileBodySchema = z.object({
	firstname: z
		.string()
		.trim()
		.min(
			1,
			(
				<Translation>{(t) => t(tokens.validation.firstNameRequired)}</Translation>
			) as unknown as string,
		),
	lastname: z
		.string()
		.min(
			1,
			(<Translation>{(t) => t(tokens.validation.lastNameRequired)}</Translation>) as unknown as string,
		),
	email: z
		.string()
		.email()
		.min(
			1,
			(<Translation>{(t) => t(tokens.validation.emailRequired)}</Translation>) as unknown as string,
		)
		.nullish(),
	cellphone: z
		.string()
		.min(
			1,
			(
				<Translation>{(t) => t(tokens.validation.cellphoneRequired)}</Translation>
			) as unknown as string,
		)
		.nullish(),
});

const ZipCodesSchema = z.object({
	id: z.number(),
	code: z.string(),
	lat: z.number(),
	lng: z.number(),
	formatted_address: z.string().nullable(),
	city: z.object({
		id: z.number(),
		name: z.string(),
		state_name: z.string(),
		state_name_short: z.string(),
		created: z.string(),
		lat: z.number(),
		lng: z.number(),
		state: z.object({
			id: z.number(),
			name: z.string(),
			country_name: z.string(),
			country_name_short: z.string(),
			name_short: z.string(),
			created: z.string(),
		}),
	}),

	created: z.string(),
});

export const ZipCodesResponseSchema = GlobalPaginationResponse.extend({
	results: z.array(ZipCodesSchema),
});
